'use client';

import { c as _c } from "react/compiler-runtime";
import { type CollisionDetection, DndContext, type DragEndEvent, type DragOverEvent, DragOverlay, type DragStartEvent, PointerSensor, pointerWithin, rectIntersection, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { type Factiii, FactiiiRequirement } from '@shared/all/domains/factiiis';
import type { Post, PostResponse } from '@shared/all/domains/posts';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import useFactiiis from '@shared/utils/hooks/useFactiiis';
import usePostsShared from '@shared/utils/hooks/usePosts';
import useSpace from '@shared/utils/hooks/useSpace';
import { type InfiniteData, keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { List, Plus, SearchX } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useRunner } from '../../../hooks/useRunnerContext';
import { useSearchScope } from '../../../hooks/useSearchScope';
import { client, type RouterOutputs } from '../../../utils/trpc';
import Button from '../../common/Button';
import { useDebouncedValue } from '../../onedrive/common';
import AddFactiiiModal from '../../spaces/AddFactiiiModal';
import BoardCard from './BoardCard';
import BoardList from './BoardList';
import CardMergeBar, { type SelectedCard } from './CardMergeBar';
type Props = {
  spaceSlug: string;
  spaceQuery: RouterOutputs['spaces']['get'];
};
type PostDragData = {
  type: 'post';
  post: Post;
  factiiiId: number;
};
type FactiiiDragData = {
  type: 'factiii';
  factiii: Factiii;
  factiiiId: number;
};
type DragData = PostDragData | FactiiiDragData;

// Placeholder columns, shown both while the board's own lists load and while a
// search is resolving — in both cases the lists area has nothing real to draw.
const BoardSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex h-full items-start gap-3 p-3 sm:gap-4 sm:p-4">{[0, 1, 2].map(_temp2)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const BoardLayout = t0 => {
  const $ = _c(77);
  const {
    spaceSlug,
    spaceQuery
  } = t0;
  const {
    getFactiiiIds
  } = useFactiiis(client);
  const queryClient = useQueryClient();
  const {
    isMember,
    isModerator
  } = useSpace();
  const [activePost, setActivePost] = useState(null);
  const [activeDragType, setActiveDragType] = useState(null);
  const [showAddList, setShowAddList] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [selectedCards, setSelectedCards] = useState(t1);
  const {
    queryKey
  } = usePostsShared(client);
  const {
    agent
  } = useRunner();
  let t2;
  let t3;
  if ($[1] !== agent || $[2] !== queryClient || $[3] !== spaceSlug) {
    t2 = () => {
      if (!agent) {
        return;
      }
      return agent.onBoardUpdate(payload => {
        if (payload.scope === "factiiis" || payload.scope === "all") {
          queryClient.invalidateQueries({
            queryKey: ["boardFactiiis", spaceSlug]
          });
        }
        if (payload.scope === "all") {
          queryClient.invalidateQueries({
            predicate: q => {
              const key = q.queryKey;
              return Array.isArray(key) && typeof key[key.length - 1] === "string" && (key[key.length - 1] as string).endsWith(`-${spaceSlug}`);
            }
          });
        } else {
          if (payload.scope === "posts") {
            const suffix = `${payload.factiiiId}-${spaceSlug}`;
            queryClient.invalidateQueries({
              predicate: q_0 => {
                const key_0 = q_0.queryKey;
                return Array.isArray(key_0) && key_0[key_0.length - 1] === suffix;
              }
            });
          }
        }
      });
    };
    t3 = [agent, queryClient, spaceSlug];
    $[1] = agent;
    $[2] = queryClient;
    $[3] = spaceSlug;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== activeDragType) {
    t4 = args => {
      if (activeDragType === "factiii") {
        return pointerWithin(args);
      }
      const pointerCollisions = pointerWithin(args);
      const directPostCollision = pointerCollisions.find(_temp3);
      if (directPostCollision) {
        return [directPostCollision];
      }
      const factiiiCollision = pointerCollisions.find(_temp4);
      if (factiiiCollision) {
        return [factiiiCollision];
      }
      return rectIntersection(args);
    };
    $[6] = activeDragType;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const customCollisionDetection = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      activationConstraint: {
        distance: 8
      }
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      activationConstraint: {
        delay: 200,
        tolerance: 5
      }
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const sensors = useSensors(useSensor(PointerSensor, t5), useSensor(TouchSensor, t6));
  let t7;
  if ($[10] !== spaceSlug) {
    t7 = ["boardFactiiis", spaceSlug];
    $[10] = spaceSlug;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== getFactiiiIds || $[13] !== spaceSlug) {
    t8 = async t9 => {
      const {
        pageParam
      } = t9;
      const {
        factiiiIds
      } = await unwrapFromResult(client.factiiis.getFactiiis.query({
        slug: spaceSlug,
        cursor: pageParam,
        type: "SPACE",
        onlyLists: true
      }));
      const factiiis = await getFactiiiIds(factiiiIds);
      return {
        factiiis
      };
    };
    $[12] = getFactiiiIds;
    $[13] = spaceSlug;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7 || $[16] !== t8) {
    t9 = {
      queryKey: t7,
      queryFn: t8,
      initialPageParam: undefined as number | undefined,
      getNextPageParam: _temp5,
      refetchOnWindowFocus: false,
      staleTime: 300000,
      gcTime: 1000
    };
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const {
    data: factiiisData,
    isLoading
  } = useInfiniteQuery(t9);
  const factiiis_0 = factiiisData?.pages.flatMap(_temp6) || [];
  const {
    scope,
    query: scopeQuery
  } = useSearchScope();
  let t10;
  if ($[18] !== scope?.kind || $[19] !== scopeQuery) {
    t10 = scope?.kind === "board" ? scopeQuery.trim() : "";
    $[18] = scope?.kind;
    $[19] = scopeQuery;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const boardQuery = t10;
  const debouncedQuery = useDebouncedValue(boardQuery, 300);
  let t11;
  if ($[21] !== debouncedQuery || $[22] !== spaceSlug) {
    t11 = ["boardSearch", spaceSlug, debouncedQuery];
    $[21] = debouncedQuery;
    $[22] = spaceSlug;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = debouncedQuery.length > 0;
  let t13;
  if ($[24] !== debouncedQuery || $[25] !== spaceSlug) {
    t13 = () => unwrapFromResult(client.board.cards.query({
      spaceSlug,
      query: debouncedQuery
    }));
    $[24] = debouncedQuery;
    $[25] = spaceSlug;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t11 || $[28] !== t12 || $[29] !== t13) {
    t14 = {
      queryKey: t11,
      enabled: t12,
      queryFn: t13,
      placeholderData: keepPreviousData,
      staleTime: 30000
    };
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const {
    data: searchData,
    isError: searchFailed
  } = useQuery(t14);
  const isSearching = boardQuery.length > 0;
  const matchesByColumn = isSearching && searchData ? new Map(searchData.columns.map(_temp8)) : null;
  const searchPending = isSearching && !matchesByColumn && !searchFailed;
  const visibleFactiiis = matchesByColumn ? factiiis_0.filter(f => (matchesByColumn.get(f.id)?.size ?? 0) > 0) : factiiis_0;
  const boardEditable = !isSearching;
  let t15;
  if ($[31] !== isModerator || $[32] !== spaceQuery) {
    t15 = isModerator(spaceQuery);
    $[31] = isModerator;
    $[32] = spaceQuery;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const canCreateFactiii = t15;
  let t16;
  if ($[34] !== isMember || $[35] !== spaceQuery) {
    t16 = isMember(spaceQuery);
    $[34] = isMember;
    $[35] = spaceQuery;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const canPost = t16;
  let t17;
  if ($[37] !== isModerator || $[38] !== spaceQuery) {
    t17 = isModerator(spaceQuery);
    $[37] = isModerator;
    $[38] = spaceQuery;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const canMove = t17;
  let t18;
  if ($[40] !== isModerator || $[41] !== spaceQuery) {
    t18 = isModerator(spaceQuery);
    $[40] = isModerator;
    $[41] = spaceQuery;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  const canDelete = t18;
  let t19;
  if ($[43] !== queryClient || $[44] !== spaceSlug) {
    t19 = () => {
      queryClient.invalidateQueries({
        queryKey: ["boardFactiiis", spaceSlug]
      });
    };
    $[43] = queryClient;
    $[44] = spaceSlug;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  const handleRefetch = t19;
  const [dragGhost, setDragGhost] = useState(null);
  let t20;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = event => {
      const {
        active
      } = event;
      const activeData = active.data.current as DragData | undefined;
      if (activeData?.type === "post") {
        setActivePost({
          post: activeData.post,
          factiiiId: activeData.factiiiId
        });
        setActiveDragType("post");
      } else {
        if (activeData?.type === "factiii") {
          setActiveDragType("factiii");
        }
      }
    };
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  const handleDragStart = t20;
  const handleDragOver = event_0 => {
    const {
      active: active_0,
      over
    } = event_0;
    const activeData_0 = active_0.data.current as DragData | undefined;
    if (!activeData_0 || activeData_0.type !== "post") {
      return;
    }
    if (!over) {
      setDragGhost(null);
      return;
    }
    const overData = over.data.current as DragData | undefined;
    if (!overData) {
      return;
    }
    const sourceFactiiiId = activeData_0.factiiiId;
    const destFactiiiId = overData.factiiiId;
    if (destFactiiiId === sourceFactiiiId) {
      setDragGhost(null);
      return;
    }
    const destCacheKey = getPostsCacheKey(destFactiiiId);
    const destData = queryClient.getQueryData(destCacheKey);
    const destPosts = (destData?.pages.flatMap(_temp9) ?? []).slice().sort(_temp10);
    let insertIndex = destPosts.length;
    if (overData.type === "post") {
      const idx = destPosts.findIndex(p => p.id === overData.post.id);
      if (idx !== -1) {
        insertIndex = idx;
      }
    }
    const rect = active_0.rect.current.translated ?? active_0.rect.current.initial;
    const height = rect?.height ?? 80;
    setDragGhost(prev => {
      if (prev && prev.destFactiiiId === destFactiiiId && prev.insertIndex === insertIndex) {
        return prev;
      }
      return {
        post: activeData_0.post,
        sourceFactiiiId,
        destFactiiiId,
        insertIndex,
        height
      };
    });
  };
  const calculateNewPosition = _temp11;
  const reorderItems = _temp12;
  const handleFactiiiReorder = async (activeData_1, overData_0) => {
    const result = reorderItems(factiiis_0, activeData_1.factiii.id, overData_0.factiii.id);
    if (!result) {
      return;
    }
    const {
      reordered: reordered_0,
      newIndex
    } = result;
    const newPos = calculateNewPosition(reordered_0[newIndex - 1]?.pos, reordered_0[newIndex + 1]?.pos);
    const updatedFactiii = {
      ...reordered_0[newIndex],
      pos: newPos
    };
    reordered_0[newIndex] = updatedFactiii;
    const cacheKey = ["boardFactiiis", spaceSlug];
    const previous = queryClient.getQueryData(cacheKey);
    queryClient.setQueryData(cacheKey, {
      pages: [{
        factiiis: reordered_0
      }],
      pageParams: [0]
    });
    const [error] = await client.factiiis.updateFactiiiPositions.mutate({
      updates: [{
        factiiiId: updatedFactiii.id,
        newPos
      }],
      spaceSlug
    });
    if (error) {
      queryClient.setQueryData(cacheKey, previous);
      return;
    }
    agent?.broadcast({
      scope: "factiiis"
    });
  };
  const getPostsCacheKey = factiiiId => {
    const baseKey = queryKey.slice(0, -1);
    return [...baseKey, `${factiiiId}-${spaceSlug}`];
  };
  let t21;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = (post, factiiiId_0, hasChanges) => {
      setSelectedCards(prev_0 => prev_0.some(s_0 => s_0.post.id === post.id) ? prev_0.filter(s => s.post.id !== post.id) : [...prev_0, {
        post,
        factiiiId: factiiiId_0,
        hasChanges
      }]);
    };
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  const toggleCardSelect = t21;
  const handleMerged = factiiiIds_0 => {
    for (const factiiiId_1 of new Set(factiiiIds_0)) {
      queryClient.invalidateQueries({
        queryKey: getPostsCacheKey(factiiiId_1)
      });
      agent?.broadcast({
        scope: "posts",
        factiiiId: factiiiId_1
      });
    }
    setSelectedCards([]);
  };
  const handlePostReorderWithinList = async (activeData_2, overData_1) => {
    const factiiiId_2 = activeData_2.factiiiId;
    const cacheKey_0 = getPostsCacheKey(factiiiId_2);
    const currentData = queryClient.getQueryData(cacheKey_0);
    if (!currentData) {
      return;
    }
    const allPosts = currentData.pages.flatMap(_temp13).slice().sort(_temp14);
    const result_0 = reorderItems(allPosts, activeData_2.post.id, overData_1.post.id);
    if (!result_0) {
      return;
    }
    const {
      reordered: reordered_1,
      newIndex: newIndex_0
    } = result_0;
    const newPos_0 = calculateNewPosition(reordered_1[newIndex_0 - 1]?.pos, reordered_1[newIndex_0 + 1]?.pos);
    const updatedPost = {
      ...reordered_1[newIndex_0],
      pos: newPos_0
    };
    reordered_1[newIndex_0] = updatedPost;
    const previous_0 = queryClient.getQueryData(cacheKey_0);
    queryClient.setQueryData(cacheKey_0, {
      ...currentData,
      pages: [{
        posts: reordered_1
      }]
    });
    const [error_0] = await client.posts.updatePostPositions.mutate({
      updates: [{
        postId: updatedPost.id,
        newPos: newPos_0
      }],
      factiiiId: factiiiId_2
    });
    if (error_0) {
      queryClient.setQueryData(cacheKey_0, previous_0);
      return;
    }
    agent?.broadcast({
      scope: "posts",
      factiiiId: factiiiId_2
    });
  };
  const handlePostMoveToList = async (post_0, sourceFactiiiId_0, destinationFactiiiId, insertIndex_0) => {
    const sourceCacheKey = getPostsCacheKey(sourceFactiiiId_0);
    const destCacheKey_0 = getPostsCacheKey(destinationFactiiiId);
    const previousSource = queryClient.getQueryData(sourceCacheKey);
    const previousDest = queryClient.getQueryData(destCacheKey_0);
    const destPosts_0 = (previousDest?.pages.flatMap(_temp15) ?? []).slice().sort(_temp16);
    const safeIndex = Math.max(0, Math.min(insertIndex_0, destPosts_0.length));
    const newPos_1 = calculateNewPosition(destPosts_0[safeIndex - 1]?.pos, destPosts_0[safeIndex]?.pos);
    const updatedPost_0 = {
      ...post_0,
      pos: newPos_1
    };
    const reorderedPosts = [...destPosts_0];
    reorderedPosts.splice(safeIndex, 0, updatedPost_0);
    queryClient.setQueryData(sourceCacheKey, old => {
      if (!old) {
        return old;
      }
      return {
        ...old,
        pages: old.pages.map(page_3 => ({
          ...page_3,
          posts: page_3.posts.filter(p_0 => p_0.id !== post_0.id)
        }))
      };
    });
    queryClient.setQueryData(destCacheKey_0, old_0 => {
      if (!old_0) {
        return {
          pages: [{
            posts: reorderedPosts
          }],
          pageParams: [undefined]
        } as InfiniteData<PostResponse>;
      }
      return {
        ...old_0,
        pages: [{
          posts: reorderedPosts
        }]
      };
    });
    const rollback = () => {
      queryClient.setQueryData(sourceCacheKey, previousSource);
      queryClient.setQueryData(destCacheKey_0, previousDest);
    };
    const [tagError] = await client.factiiis.tag.mutate({
      postId: post_0.id,
      removeFactiiiIds: [sourceFactiiiId_0],
      addFactiiis: [{
        factiiiId: destinationFactiiiId
      }]
    });
    if (tagError) {
      rollback();
      return;
    }
    const [posError] = await client.posts.updatePostPositions.mutate({
      updates: [{
        postId: post_0.id,
        newPos: newPos_1
      }],
      factiiiId: destinationFactiiiId
    });
    if (posError) {
      rollback();
      return;
    }
    agent?.broadcast({
      scope: "posts",
      factiiiId: sourceFactiiiId_0
    });
    agent?.broadcast({
      scope: "posts",
      factiiiId: destinationFactiiiId
    });
  };
  const handleDragEnd = async event_1 => {
    const {
      active: active_1,
      over: over_0
    } = event_1;
    setActivePost(null);
    setActiveDragType(null);
    const ghost = dragGhost;
    setDragGhost(null);
    if (!over_0) {
      return;
    }
    const activeData_3 = active_1.data.current as DragData | undefined;
    const overData_2 = over_0.data.current as DragData | undefined;
    if (!activeData_3) {
      return;
    }
    if (activeData_3.type === "factiii" && overData_2?.type === "factiii" && active_1.id !== over_0.id) {
      await handleFactiiiReorder(activeData_3, overData_2);
      return;
    }
    if (activeData_3.type !== "post") {
      return;
    }
    if (ghost && ghost.sourceFactiiiId === activeData_3.factiiiId && ghost.destFactiiiId !== activeData_3.factiiiId) {
      await handlePostMoveToList(activeData_3.post, ghost.sourceFactiiiId, ghost.destFactiiiId, ghost.insertIndex);
      return;
    }
    if (overData_2?.type === "post" && activeData_3.factiiiId === overData_2.factiiiId && active_1.id !== over_0.id) {
      await handlePostReorderWithinList(activeData_3, overData_2);
    }
  };
  if (isLoading) {
    let t22;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-background-primary"><BoardSkeleton /></div>;
      $[48] = t22;
    } else {
      t22 = $[48];
    }
    return t22;
  }
  const t22 = "relative flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-background-primary";
  const T0 = DndContext;
  const t23 = "min-h-0 flex-1 overflow-x-auto overflow-y-hidden";
  const t24 = searchPending ? <BoardSkeleton /> : isSearching && (searchFailed || visibleFactiiis.length === 0) ? <div className="flex size-full flex-col items-center justify-center gap-3 px-4 text-center"><div className="flex size-12 items-center justify-center rounded-2xl bg-background-secondary text-textSecondary"><SearchX size={22} strokeWidth={1.5} /></div><p className="text-sm text-textSecondary">{searchFailed ? <>Couldn’t search this board. Try again.</> : <>No cards match{" "}<span className="font-emphasis text-textPrimary">“{boardQuery}”</span></>}</p></div> : <div className="flex h-full items-start gap-3 p-3 sm:gap-4 sm:p-4">{factiiis_0.length === 0 ? <div className="flex size-full flex-col items-center justify-center gap-4 px-4 sm:gap-6"><div className="w-full max-w-sm rounded-xl bg-background-secondary p-6 text-center shadow-lg sm:p-12"><div className="mb-4 flex justify-center sm:mb-6"><div className="rounded-xl bg-primary/10 p-3 sm:p-4"><List size={36} className="text-primary sm:size-12" /></div></div><h2 className="mb-2 text-xl font-semibold text-textPrimary sm:mb-3 sm:text-2xl">Your board is empty</h2><p className="mb-4 text-sm text-textSecondary sm:mb-6">{canCreateFactiii ? "Create your first list to get started organizing posts" : "No lists have been added to this board yet"}</p>{canCreateFactiii && <div className="w-full"><Button text="Create First List" variant="PRIMARY" icon={<Plus size={18} />} onClick={() => setShowAddList(true)} classNames="flex items-center gap-2 font-semibold w-full justify-center" /></div>}</div></div> : <><SortableContext items={visibleFactiiis.map(_temp17)} strategy={horizontalListSortingStrategy}>{visibleFactiiis.map(factiii => <BoardList key={factiii.id} factiii={factiii} spaceSlug={spaceSlug} canPost={canPost && boardEditable} canMove={canMove && boardEditable} canDelete={canDelete && boardEditable} allColumns={factiiis_0} matchedPostIds={matchesByColumn?.get(factiii.id) ?? null} selectedIds={selectedCards.map(_temp18)} selectionHasChanges={selectedCards[0]?.hasChanges ?? null} onToggleSelect={toggleCardSelect} ghost={dragGhost && dragGhost.destFactiiiId === factiii.id ? {
          post: dragGhost.post,
          insertIndex: dragGhost.insertIndex,
          height: dragGhost.height
        } : null} />)}</SortableContext>{canCreateFactiii && boardEditable && <div className="xs:w-64 flex w-[75vw] shrink-0 items-start sm:w-72 md:w-80"><button data-id="board-add-list" onClick={() => setShowAddList(true)} className="flex w-full items-center gap-2 rounded-xl bg-background-secondary/60 px-3 py-2.5 text-sm font-semibold text-textPrimary backdrop-blur-sm transition-all hover:bg-background-light sm:px-4 sm:py-3"><Plus size={18} /><span>Add another list</span></button></div>}</>}</div>;
  let t25;
  if ($[49] !== t24) {
    t25 = <div className={t23}>{t24}</div>;
    $[49] = t24;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== activePost || $[52] !== spaceSlug) {
    t26 = activePost ? <div className="xs:w-64 w-[75vw] rotate-3 opacity-90 sm:w-72 sm:rotate-6 md:w-80"><BoardCard post={activePost.post} factiiiId={activePost.factiiiId} spaceSlug={spaceSlug} /></div> : null;
    $[51] = activePost;
    $[52] = spaceSlug;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== t26) {
    t27 = <DragOverlay>{t26}</DragOverlay>;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== T0 || $[57] !== customCollisionDetection || $[58] !== handleDragEnd || $[59] !== handleDragOver || $[60] !== handleDragStart || $[61] !== sensors || $[62] !== t25 || $[63] !== t27) {
    t28 = <T0 sensors={sensors} collisionDetection={customCollisionDetection} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>{t25}{t27}</T0>;
    $[56] = T0;
    $[57] = customCollisionDetection;
    $[58] = handleDragEnd;
    $[59] = handleDragOver;
    $[60] = handleDragStart;
    $[61] = sensors;
    $[62] = t25;
    $[63] = t27;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== handleMerged || $[66] !== selectedCards) {
    t29 = selectedCards.length > 0 && <CardMergeBar selected={selectedCards} onClear={() => setSelectedCards([])} onMerged={handleMerged} />;
    $[65] = handleMerged;
    $[66] = selectedCards;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== agent || $[69] !== handleRefetch || $[70] !== showAddList || $[71] !== spaceSlug) {
    t30 = showAddList && <AddFactiiiModal type="SPACE" slug={spaceSlug} forcedRequirements={[FactiiiRequirement.LIST]} refetch={handleRefetch} closeHandler={factiii_0 => {
      setShowAddList(false);
      if (factiii_0) {
        handleRefetch();
        agent?.broadcast({
          scope: "factiiis"
        });
      }
    }} />;
    $[68] = agent;
    $[69] = handleRefetch;
    $[70] = showAddList;
    $[71] = spaceSlug;
    $[72] = t30;
  } else {
    t30 = $[72];
  }
  let t31;
  if ($[73] !== t28 || $[74] !== t29 || $[75] !== t30) {
    t31 = <div className={t22}>{t28}{t29}{t30}</div>;
    $[73] = t28;
    $[74] = t29;
    $[75] = t30;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  return t31;
};
export default BoardLayout;
function _temp(_, i) {
  return <div key={i} className="overflow-hidden rounded-lg bg-background-primary p-3"><div className="relative overflow-hidden"><div className="mb-2 h-4 w-3/4 rounded bg-background-light" /><div className="mb-1 h-3 w-full rounded bg-background-light" /><div className="h-3 w-1/2 rounded bg-background-light" /><div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/10 to-transparent" /></div></div>;
}
function _temp2(col) {
  return <div key={col} className="xs:w-64 flex w-[75vw] shrink-0 flex-col rounded-xl bg-background-secondary shadow-md sm:w-72 md:w-80"><div className="flex items-center gap-2.5 border-b border-ds-border-subtle px-3 py-2.5"><div className="size-6 animate-pulse rounded-full bg-background-light" /><div className="h-4 w-24 animate-pulse rounded bg-background-light" /></div><div className="space-y-2 p-2">{Array.from({
        length: col === 0 ? 4 : col === 1 ? 3 : 2
      }).map(_temp)}</div></div>;
}
function _temp3(c) {
  return String(c.id).startsWith("post-");
}
function _temp4(c_0) {
  return String(c_0.id).startsWith("factiii-");
}
function _temp5(lastPage) {
  if (lastPage.factiiis.length === 10) {
    return lastPage.factiiis[lastPage.factiiis.length - 1].id;
  }
}
function _temp6(page) {
  return page.factiiis;
}
function _temp7(card) {
  return card.postId;
}
function _temp8(column) {
  return [column.id, new Set(column.cards.map(_temp7))];
}
function _temp9(page_0) {
  return page_0.posts;
}
function _temp10(a, b) {
  return (a.pos ?? Number.MAX_SAFE_INTEGER) - (b.pos ?? Number.MAX_SAFE_INTEGER);
}
function _temp11(prevPos, nextPos) {
  if (prevPos == null && nextPos == null) {
    return 65535;
  }
  if (prevPos == null) {
    return (nextPos ?? 65535) / 2;
  }
  if (nextPos == null) {
    return prevPos + 65535;
  }
  return (prevPos + nextPos) / 2;
}
function _temp12(items, activeId, overId) {
  const activeIndex = items.findIndex(item => item.id === activeId);
  const overIndex = items.findIndex(item_0 => item_0.id === overId);
  if (activeIndex === -1 || overIndex === -1) {
    return null;
  }
  const reordered = arrayMove(items, activeIndex, overIndex);
  return {
    reordered,
    newIndex: overIndex
  };
}
function _temp13(page_1) {
  return page_1.posts;
}
function _temp14(a_0, b_0) {
  return (a_0.pos ?? Number.MAX_SAFE_INTEGER) - (b_0.pos ?? Number.MAX_SAFE_INTEGER);
}
function _temp15(page_2) {
  return page_2.posts;
}
function _temp16(a_1, b_1) {
  return (a_1.pos ?? Number.MAX_SAFE_INTEGER) - (b_1.pos ?? Number.MAX_SAFE_INTEGER);
}
function _temp17(f_0) {
  return `factiii-${f_0.id}`;
}
function _temp18(s_1) {
  return s_1.post.id;
}